import { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import AboutPage from './pages/AboutPage';
import PortfolioPage from './pages/PortfolioPage';
import ContactPage from './pages/ContactPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import Footer from './components/Footer';
import StarfieldBackground from './components/StarfieldBackground';

function App() {
    const [darkMode, setDarkMode] = useState(true); // Set default to dark mode

    useEffect(() => {
        // Check for saved theme preference or default to dark
        const savedTheme = localStorage.getItem('theme');

        // Use saved theme, or default to dark if no preference is saved
        const shouldUseDark = savedTheme ? savedTheme === 'dark' : true;

        setDarkMode(shouldUseDark);

        if (shouldUseDark) {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, []);

    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add('dark');
            localStorage.setItem('theme', 'dark');
        } else {
            document.documentElement.classList.remove('dark');
            localStorage.setItem('theme', 'light');
        }
    }, [darkMode]);

    // Add smooth scrolling CSS and mobile menu styles
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            html {
                scroll-behavior: smooth;
            }
            
            * {
                scroll-behavior: smooth;
            }
            
            /* Custom scrollbar */
            ::-webkit-scrollbar {
                width: 8px;
            }
            
            ::-webkit-scrollbar-track {
                background: transparent;
            }
            
            ::-webkit-scrollbar-thumb {
                background: #10b981;
                border-radius: 4px;
            }
            
            ::-webkit-scrollbar-thumb:hover {
                background: #059669;
            }

            /* Starfield Animation Keyframes */
            @keyframes twinkle {
                0%, 100% { opacity: 0.3; transform: scale(1); }
                50% { opacity: 1; transform: scale(1.2); }
            }

            @keyframes float {
                0%, 100% { transform: translateY(0px) rotate(0deg); }
                33% { transform: translateY(-10px) rotate(120deg); }
                66% { transform: translateY(5px) rotate(240deg); }
            }

            @keyframes drift {
                0% { transform: translateX(0px); }
                50% { transform: translateX(20px); }
                100% { transform: translateX(0px); }
            }

            @keyframes shooting-star {
                0% { 
                    transform: translateX(-100px) translateY(0px) rotate(45deg);
                    opacity: 0;
                }
                10% { opacity: 1; }
                90% { opacity: 1; }
                100% { 
                    transform: translateX(100vw) translateY(50px) rotate(45deg);
                    opacity: 0;
                }
            }

            /* Star classes for CSS animations */
            .star {
                position: absolute;
                background: white;
                border-radius: 50%;
                animation: twinkle 3s infinite ease-in-out;
            }

            .star-small {
                width: 1px;
                height: 1px;
                animation-duration: 2s;
            }

            .star-medium {
                width: 2px;
                height: 2px;
                animation-duration: 3s;
                box-shadow: 0 0 6px rgba(255, 255, 255, 0.8);
            }

            .star-large {
                width: 3px;
                height: 3px;
                animation-duration: 4s;
                box-shadow: 0 0 10px rgba(255, 255, 255, 0.9);
            }

            .floating-particle {
                animation: float 6s infinite ease-in-out;
            }

            .drifting-star {
                animation: drift 8s infinite ease-in-out;
            }

            .shooting-star {
                position: absolute;
                width: 2px;
                height: 2px;
                background: linear-gradient(45deg, white, transparent);
                border-radius: 50%;
                animation: shooting-star 3s infinite linear;
            }

            /* Mobile menu animations */
            .mobile-menu-enter {
                transform: translateX(100%);
            }
            
            .mobile-menu-enter-active {
                transform: translateX(0);
                transition: transform 300ms ease-in-out;
            }
            
            .mobile-menu-exit {
                transform: translateX(0);
            }
            
            .mobile-menu-exit-active {
                transform: translateX(100%);
                transition: transform 300ms ease-in-out;
            }

            /* Prevent body scroll when mobile menu is open */
            body.mobile-menu-open {
                overflow: hidden;
                position: fixed;
                width: 100%;
            }

            /* Touch-friendly interactions */
            @media (max-width: 768px) {
                button, a, [role="button"] {
                    min-height: 44px;
                    min-width: 44px;
                }
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const toggleDarkMode = () => {
        setDarkMode(!darkMode);
    };

    return (
        <div className="bg-background-light dark:bg-background-dark text-gray-800 dark:text-gray-300 font-display transition-colors duration-300 relative">
            {/* Animated Starfield Background */}
            <StarfieldBackground />

            {/* Main Content */}
            <div className="relative z-10">
                <Navbar toggleDarkMode={toggleDarkMode} />
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/portfolio" element={<PortfolioPage />} />
                    <Route path="/portfolio/:projectId" element={<ProjectDetailPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                </Routes>
                <Footer />
            </div>
        </div>
    );
}

export default App;